import { createClient } from '@supabase/supabase-js';
import { createContext, useMemo, useState, useRef, useEffect, useCallback, useContext } from 'react';
import { jsx } from 'react/jsx-runtime';

// src/OmniAuthProvider.tsx

// src/cookieSession.ts
var COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
var COOKIE_CHUNK_SIZE = 3500;
var COOKIE_RECHECK_MS = 6e4;
function normaliseCookieDomain(domain) {
  return domain.trim().replace(/^\.+/, "").replace(/\.+$/, "").toLowerCase();
}
function hostUnderDomain(host, cookieDomain) {
  const d = normaliseCookieDomain(cookieDomain);
  const h = host.trim().toLowerCase().replace(/\.+$/, "");
  if (!d || !h) return false;
  return h === d || h.endsWith(`.${d}`);
}
function cookieNameFor(key) {
  return key.replace(/[^A-Za-z0-9_-]/g, (c) => `_${c.charCodeAt(0).toString(16)}`);
}
function readJar(doc) {
  const jar = /* @__PURE__ */ new Map();
  const raw = doc.cookie || "";
  for (const part of raw.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const name = part.slice(0, eq).trim();
    if (!name || jar.has(name)) continue;
    jar.set(name, part.slice(eq + 1).trim());
  }
  return jar;
}
function createCookieStorage(opts) {
  const domain = normaliseCookieDomain(opts.domain);
  if (!domain) throw new Error("createCookieStorage: domain is required");
  const maxAge = opts.maxAgeSeconds ?? COOKIE_MAX_AGE_SECONDS;
  const chunkSize = opts.chunkSize ?? COOKIE_CHUNK_SIZE;
  const doc = () => {
    if (opts.doc) return opts.doc;
    if (typeof document === "undefined") throw new Error("createCookieStorage: no document");
    return document;
  };
  const attrs = (age) => `; Domain=.${domain}; Path=/; Max-Age=${age}; SameSite=Lax; Secure`;
  const write = (name, value) => {
    doc().cookie = `${name}=${value}${attrs(maxAge)}`;
  };
  const erase = (name) => {
    doc().cookie = `${name}=${attrs(0)}`;
  };
  const chunkNames = (base, jar) => {
    const names = [];
    for (const name of jar.keys()) {
      const dot = name.lastIndexOf(".");
      if (dot > 0 && name.slice(0, dot) === base && /^\d+$/.test(name.slice(dot + 1))) {
        names.push(name);
      }
    }
    return names;
  };
  return {
    domain,
    getItem(key) {
      const base = cookieNameFor(key);
      const jar = readJar(doc());
      if (!jar.has(`${base}.0`)) return null;
      let encoded = "";
      for (let i = 0; jar.has(`${base}.${i}`); i += 1) encoded += jar.get(`${base}.${i}`);
      try {
        const value = decodeURIComponent(encoded);
        if (/^[[{]/.test(value)) JSON.parse(value);
        return value;
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      const base = cookieNameFor(key);
      const encoded = encodeURIComponent(value);
      const count = Math.max(1, Math.ceil(encoded.length / chunkSize));
      for (let i = 0; i < count; i += 1) {
        write(`${base}.${i}`, encoded.slice(i * chunkSize, (i + 1) * chunkSize));
      }
      for (const name of chunkNames(base, readJar(doc()))) {
        const n = Number(name.slice(name.lastIndexOf(".") + 1));
        if (n >= count) erase(name);
      }
    },
    removeItem(key) {
      const base = cookieNameFor(key);
      for (const name of chunkNames(base, readJar(doc()))) erase(name);
    },
    clearAll(prefix) {
      const p = cookieNameFor(prefix);
      for (const name of readJar(doc()).keys()) {
        const dot = name.lastIndexOf(".");
        if (dot > 0 && name.startsWith(p) && /^\d+$/.test(name.slice(dot + 1))) erase(name);
      }
    }
  };
}
function chooseSessionStorage(args) {
  if (!args.cookieDomain || !hostUnderDomain(args.host, args.cookieDomain)) return void 0;
  return createCookieStorage({ domain: args.cookieDomain, doc: args.doc });
}
function watchCookieSession(args) {
  const target = args.target ?? (typeof window !== "undefined" ? window : void 0);
  const every = args.setInterval ?? ((fn, ms) => setInterval(fn, ms));
  const stopEvery = args.clearInterval ?? ((id2) => clearInterval(id2));
  const later = args.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
  let stopped = false;
  let confirming = false;
  const check = () => {
    if (stopped || confirming) return;
    const inCookie = args.read() != null;
    const held = args.hasSession();
    if (held && !inCookie) {
      confirming = true;
      later(() => {
        confirming = false;
        if (stopped) return;
        if (args.read() == null && args.hasSession()) args.onGone();
      }, args.confirmMs ?? 250);
      return;
    }
    if (!held && inCookie) args.onBack?.();
  };
  target?.addEventListener("focus", check);
  target?.addEventListener("visibilitychange", check);
  const id = every(check, args.intervalMs ?? COOKIE_RECHECK_MS);
  return () => {
    stopped = true;
    target?.removeEventListener("focus", check);
    target?.removeEventListener("visibilitychange", check);
    stopEvery(id);
  };
}
var APP_IDS = ["mail", "crm", "pm"];
var STORAGE_KEY = {
  mail: "omni-auth-mail",
  crm: "omni-auth-crm",
  pm: "omni-auth-pm"
};
var SHARED_STORAGE_KEY = "omni-auth";
var OmniAuthContext = createContext(null);
function makeClient(url, anonKey, storageKey, persistTo, dbSchema, extras = {}) {
  return createClient(url, anonKey, {
    ...dbSchema ? { db: { schema: dbSchema } } : {},
    auth: {
      storageKey,
      persistSession: persistTo !== "memory",
      autoRefreshToken: true,
      // The callback route lives at <shellUrl>/auth/callback?app=<id>.
      // Supabase parses the URL hash on the very first mount of that page.
      detectSessionInUrl: true,
      // Opt-in only: both keys are absent unless the instance asks, so the
      // options object is the same one every other instance builds today.
      ...extras.storage ? { storage: extras.storage } : {},
      ...extras.flowType ? { flowType: extras.flowType } : {}
    }
  });
}
function makeDataClient(url, anonKey, dbSchema, authOwner) {
  return createClient(url, anonKey, {
    db: { schema: dbSchema },
    accessToken: async () => (await authOwner.auth.getSession()).data.session?.access_token ?? null
  });
}
function buildSessionView(state, loading) {
  const signedIn = {
    mail: state.mail.session != null,
    crm: state.crm.session != null,
    pm: state.pm.session != null
  };
  const signedInCount = Number(signedIn.mail) + Number(signedIn.crm) + Number(signedIn.pm);
  const primarySession = state.mail.session ?? state.crm.session ?? state.pm.session ?? null;
  const primaryUser = primarySession?.user ?? null;
  return {
    isFullyAuthed: signedInCount === 3,
    isPartiallyAuthed: signedInCount > 0 && signedInCount < 3,
    signedIn,
    primaryEmail: primaryUser?.email ?? null,
    primaryUser,
    loading
  };
}
function OmniAuthProvider({
  projects,
  shellUrl,
  persistTo = "localStorage",
  cookieDomain,
  flowType,
  children
}) {
  const cookieStorage = useMemo(
    () => persistTo === "cookie" && typeof window !== "undefined" ? chooseSessionStorage({ cookieDomain, host: window.location.hostname }) : void 0,
    [persistTo, cookieDomain]
  );
  const { clients, authClients } = useMemo(
    () => {
      const singleProject = projects.mail.url === projects.crm.url && projects.crm.url === projects.pm.url && projects.mail.anonKey === projects.crm.anonKey && projects.crm.anonKey === projects.pm.anonKey;
      const extras = { storage: cookieStorage, flowType };
      if (singleProject) {
        const shared = makeClient(
          projects.mail.url,
          projects.mail.anonKey,
          SHARED_STORAGE_KEY,
          persistTo,
          void 0,
          extras
        );
        const bySchema = /* @__PURE__ */ new Map();
        const forSlot = (app) => {
          const schema = projects[app].dbSchema?.trim();
          if (!schema) return shared;
          if (!bySchema.has(schema)) {
            bySchema.set(
              schema,
              makeDataClient(
                projects[app].url,
                projects[app].anonKey,
                schema,
                shared
              )
            );
          }
          return bySchema.get(schema);
        };
        return {
          clients: { mail: forSlot("mail"), crm: forSlot("crm"), pm: forSlot("pm") },
          authClients: { mail: shared, crm: shared, pm: shared }
        };
      }
      const perApp = (app) => makeClient(
        projects[app].url,
        projects[app].anonKey,
        STORAGE_KEY[app],
        persistTo,
        projects[app].dbSchema?.trim() || void 0,
        extras
      );
      const separate = { mail: perApp("mail"), crm: perApp("crm"), pm: perApp("pm") };
      return { clients: separate, authClients: separate };
    },
    [
      projects.mail.url,
      projects.mail.anonKey,
      projects.mail.dbSchema,
      projects.crm.url,
      projects.crm.anonKey,
      projects.crm.dbSchema,
      projects.pm.url,
      projects.pm.anonKey,
      projects.pm.dbSchema,
      persistTo,
      cookieStorage,
      flowType
    ]
  );
  const [sessions, setSessions] = useState({
    mail: null,
    crm: null,
    pm: null
  });
  const [loading, setLoading] = useState(true);
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;
  useEffect(() => {
    let mounted = true;
    let pending = APP_IDS.length;
    const subs = APP_IDS.map((app) => {
      void authClients[app].auth.getSession().then(({ data: data2 }) => {
        if (!mounted) return;
        setSessions((prev) => ({ ...prev, [app]: data2.session }));
        pending -= 1;
        if (pending === 0) setLoading(false);
      });
      const { data } = authClients[app].auth.onAuthStateChange((_event, session) => {
        if (!mounted) return;
        setSessions((prev) => ({ ...prev, [app]: session }));
      });
      return data.subscription;
    });
    return () => {
      mounted = false;
      subs.forEach((s) => s.unsubscribe());
    };
  }, [authClients]);
  useEffect(() => {
    if (!cookieStorage) return;
    const owners = Array.from(
      /* @__PURE__ */ new Set([authClients.mail, authClients.crm, authClients.pm])
    );
    const keys = owners.length === 1 ? [SHARED_STORAGE_KEY] : APP_IDS.map((app) => STORAGE_KEY[app]);
    return watchCookieSession({
      hasSession: () => APP_IDS.some((app) => sessionsRef.current[app] != null),
      read: () => {
        for (const key of keys) {
          const v = cookieStorage.getItem(key);
          if (v != null) return v;
        }
        return null;
      },
      onGone: () => {
        setSessions({ mail: null, crm: null, pm: null });
        owners.forEach((c) => void c.auth.signOut({ scope: "local" }));
      },
      onBack: () => {
        APP_IDS.forEach((app) => {
          void authClients[app].auth.getSession().then(({ data }) => {
            setSessions((prev) => ({ ...prev, [app]: data.session }));
          });
        });
      }
    });
  }, [authClients, cookieStorage]);
  const state = useMemo(
    () => ({
      mail: { session: sessions.mail, client: clients.mail },
      crm: { session: sessions.crm, client: clients.crm },
      pm: { session: sessions.pm, client: clients.pm }
    }),
    [clients, sessions]
  );
  const sessionView = useMemo(
    () => buildSessionView(state, loading),
    [state, loading]
  );
  const getClient = useCallback(
    (app) => state[app].client,
    [state]
  );
  const callbackFor = useCallback(
    (app) => new URL(`/auth/callback?app=${app}`, shellUrl).toString(),
    [shellUrl]
  );
  const signIn = useCallback(
    async (email) => {
      const uniqueClients = /* @__PURE__ */ new Map();
      for (const app of APP_IDS) {
        if (!uniqueClients.has(authClients[app])) uniqueClients.set(authClients[app], app);
      }
      const results = await Promise.all(
        Array.from(
          uniqueClients,
          ([client, app]) => client.auth.signInWithOtp({
            email,
            options: { emailRedirectTo: callbackFor(app) }
          })
        )
      );
      const refused = results.find((r) => r.error)?.error;
      if (refused) throw new Error(refused.message || "Sign-in failed. Try again.");
    },
    [authClients, callbackFor]
  );
  const signOut = useCallback(async () => {
    const uniqueClients = /* @__PURE__ */ new Set([
      authClients.mail,
      authClients.crm,
      authClients.pm
    ]);
    if (cookieStorage) {
      try {
        await Promise.all(
          Array.from(uniqueClients, (c) => c.auth.signOut({ scope: "local" }))
        );
      } finally {
        cookieStorage.clearAll(SHARED_STORAGE_KEY);
      }
      return;
    }
    await Promise.all(Array.from(uniqueClients, (c) => c.auth.signOut()));
  }, [authClients, cookieStorage]);
  const refreshSession = useCallback(
    async (app) => {
      const { data } = await authClients[app].auth.getSession();
      setSessions((prev) => ({ ...prev, [app]: data.session }));
    },
    [authClients]
  );
  const value = useMemo(
    () => ({
      state,
      session: sessionView,
      getClient,
      signIn,
      signOut,
      refreshSession,
      shellUrl
    }),
    [state, sessionView, getClient, signIn, signOut, refreshSession, shellUrl]
  );
  return /* @__PURE__ */ jsx(OmniAuthContext.Provider, { value, children });
}
var STORAGE_KEY2 = {
  mail: "omni-auth-mail",
  crm: "omni-auth-crm",
  pm: "omni-auth-pm"
};
function makeClient2(url, anonKey, storageKey, persistTo, dbSchema) {
  return createClient(url, anonKey, {
    ...dbSchema ? { db: { schema: dbSchema } } : {},
    auth: {
      storageKey,
      persistSession: persistTo !== "memory",
      autoRefreshToken: true,
      detectSessionInUrl: true
    }
  });
}
function makeRefusingClient(app) {
  const handler = {
    get() {
      throw new Error(
        `StandaloneOmniAuthProvider: useSupabase('${app}') is unavailable in standalone mode. Only the configured app is exposed. Use the multi-session OmniAuthProvider in the shell.`
      );
    }
  };
  return new Proxy({}, handler);
}
function StandaloneOmniAuthProvider({
  app,
  project,
  shellUrl,
  persistTo = "localStorage",
  children
}) {
  const clients = useMemo(() => {
    const map = {
      mail: makeRefusingClient("mail"),
      crm: makeRefusingClient("crm"),
      pm: makeRefusingClient("pm")
    };
    map[app] = makeClient2(
      project.url,
      project.anonKey,
      STORAGE_KEY2[app],
      persistTo,
      project.dbSchema?.trim() || void 0
    );
    return map;
  }, [app, project.url, project.anonKey, project.dbSchema, persistTo]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let mounted = true;
    void clients[app].auth.getSession().then(({ data: data2 }) => {
      if (!mounted) return;
      setSession(data2.session);
      setLoading(false);
    });
    const { data } = clients[app].auth.onAuthStateChange((_event, s) => {
      if (!mounted) return;
      setSession(s);
    });
    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, [clients, app]);
  const state = useMemo(() => {
    const make = (id) => ({
      session: id === app ? session : null,
      client: clients[id]
    });
    return {
      mail: make("mail"),
      crm: make("crm"),
      pm: make("pm")
    };
  }, [app, clients, session]);
  const sessionView = useMemo(() => {
    const signedIn = {
      mail: false,
      crm: false,
      pm: false
    };
    signedIn[app] = session != null;
    const primaryUser = session?.user ?? null;
    return {
      isFullyAuthed: false,
      isPartiallyAuthed: session != null,
      signedIn,
      primaryEmail: primaryUser?.email ?? null,
      primaryUser,
      loading
    };
  }, [app, session, loading]);
  const getClient = useCallback(
    (id) => state[id].client,
    [state]
  );
  const callbackFor = useCallback(
    () => new URL("/auth/callback", shellUrl).toString(),
    [shellUrl]
  );
  const signIn = useCallback(
    async (email) => {
      const { error } = await clients[app].auth.signInWithOtp({
        email,
        options: { emailRedirectTo: callbackFor() }
      });
      if (error) throw new Error(error.message || "Sign-in failed. Try again.");
    },
    [clients, app, callbackFor]
  );
  const signOut = useCallback(async () => {
    await clients[app].auth.signOut();
  }, [clients, app]);
  const refreshSession = useCallback(
    async (which) => {
      if (which !== app) return;
      const { data } = await clients[app].auth.getSession();
      setSession(data.session);
    },
    [clients, app]
  );
  const value = useMemo(
    () => ({
      state,
      session: sessionView,
      getClient,
      signIn,
      signOut,
      refreshSession,
      shellUrl
    }),
    [state, sessionView, getClient, signIn, signOut, refreshSession, shellUrl]
  );
  return /* @__PURE__ */ jsx(OmniAuthContext.Provider, { value, children });
}
function useOmniSession() {
  const ctx = useContext(OmniAuthContext);
  if (!ctx) {
    throw new Error("useOmniSession must be used inside <OmniAuthProvider>");
  }
  return ctx.session;
}
function useSupabase(app) {
  const ctx = useContext(OmniAuthContext);
  if (!ctx) {
    throw new Error("useSupabase must be used inside <OmniAuthProvider>");
  }
  return ctx.getClient(app);
}
function useOmniAuth() {
  const ctx = useContext(OmniAuthContext);
  if (!ctx) {
    throw new Error("useOmniAuth must be used inside <OmniAuthProvider>");
  }
  return ctx;
}

export { OmniAuthContext, OmniAuthProvider, StandaloneOmniAuthProvider, chooseSessionStorage, createCookieStorage, hostUnderDomain, normaliseCookieDomain, useOmniAuth, useOmniSession, useSupabase, watchCookieSession };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map